import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
    static values = {
        text: String
    }

    connect() {
        this.tooltip = null
        this.isMobile = window.innerWidth <= 768
        this.showTooltipBound = this.showTooltip.bind(this)
        this.hideTooltipBound = this.hideTooltip.bind(this)
        this.handleClickBound = this.handleClick.bind(this)
        this.handleMouseLeaveBound = this.handleMouseLeave.bind(this)
        this.handleGlobalClickBound = this.handleGlobalClick.bind(this)
        
        if (this.isMobile) {
            // Mobile: click to toggle
            this.element.addEventListener('click', this.handleClickBound)
            // Prevent hover on mobile
            this.element.addEventListener('mouseenter', (e) => e.preventDefault())
        } else {
            // Desktop: hover functionality
            this.element.addEventListener('mouseenter', this.showTooltipBound)
            this.element.addEventListener('mouseleave', this.handleMouseLeaveBound)
            // Also keep click functionality for desktop as backup
            this.element.addEventListener('click', this.handleClickBound)
        }
        
        // Global click to hide tooltip
        document.addEventListener('click', this.handleGlobalClickBound)
    }

    disconnect() {
        if (this.tooltip) {
            this.tooltip.remove()
        }
        this.element.removeEventListener('mouseenter', this.showTooltipBound)
        this.element.removeEventListener('mouseleave', this.handleMouseLeaveBound)
        this.element.removeEventListener('click', this.handleClickBound)
        document.removeEventListener('click', this.handleGlobalClickBound)
    }

    handleClick(event) {
        event.preventDefault()
        event.stopPropagation()
        
        // For mobile or when tooltip is already visible, toggle it
        if (this.isMobile || (this.tooltip && this.isTooltipVisible)) {
            if (this.tooltip && this.isTooltipVisible) {
                this.hideTooltip()
            } else {
                this.showTooltip()
            }
        } else {
            // For desktop, show tooltip on click as backup
            this.showTooltip()
        }
    }

    handleGlobalClick(event) {
        if (this.tooltip && this.isTooltipVisible) {
            // Hide if clicking outside tooltip and trigger
            if (!this.element.contains(event.target) && !this.tooltip.contains(event.target)) {
                this.hideTooltip()
            }
        }
    }

    showTooltip() {
        // Hide any other visible tooltips
        document.querySelectorAll('.filter-tooltip.is-visible').forEach(tooltip => {
            if (tooltip !== this.tooltip) {
                tooltip.classList.remove('is-visible')
                tooltip.classList.add('is-hiding')
                setTimeout(() => {
                    tooltip.style.display = 'none'
                    tooltip.classList.remove('is-hiding')
                }, 150)
            }
        })

        if (!this.tooltip) {
            this.tooltip = document.createElement('div')
            this.tooltip.className = 'filter-tooltip'
            this.tooltip.style.position = 'absolute'
            this.tooltip.style.zIndex = '9999'

            this.tooltip.innerHTML = `
                <div class="tooltip-content">
                    <div class="tooltip-text">${this.textValue}</div>
                </div>
            `
            
            document.body.appendChild(this.tooltip)

            
            if (!this.isMobile) {
                this.tooltip.addEventListener('mouseenter', () => this.tooltipMouseEnter())
                this.tooltip.addEventListener('mouseleave', () => this.tooltipMouseLeave())
            }

        }

        const rect = this.element.getBoundingClientRect()
        const windowWidth = window.innerWidth
        const windowHeight = window.innerHeight
        const scrollX = window.scrollX
        const scrollY = window.scrollY
        const isMobile = windowWidth <= 768

        this.tooltip.style.display = 'block'
        

        if (this.isMobile) {
            // Mobile: center tooltip above trigger
            const tooltipWidth = 280
            let left = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2)
            
            // Keep within viewport
            if (left < 10) left = 10
            if (left + tooltipWidth > windowWidth - 10) left = windowWidth - tooltipWidth - 10
            
            this.tooltip.style.left = `${left}px`
            this.tooltip.style.top = `${rect.top + scrollY - 20}px`
            this.tooltip.style.width = `${Math.min(tooltipWidth, windowWidth - 20)}px`
        } else {
            // Desktop: to the right of trigger
            this.tooltip.style.left = `${rect.right + scrollX + 10}px`
            this.tooltip.style.top = `${rect.top + scrollY}px`
            this.tooltip.style.width = '280px'
            
            // Check if tooltip goes outside viewport
            const tooltipRect = this.tooltip.getBoundingClientRect()
            if (tooltipRect.right > windowWidth) {
                this.tooltip.style.left = `${windowWidth - 290 + scrollX}px`
            }
            if (tooltipRect.left < 0) {
                this.tooltip.style.left = `${10 + scrollX}px`
            }
        }

        // Show with animation - immediate
        this.tooltip.classList.remove('is-hiding')
        requestAnimationFrame(() => {
            this.tooltip.classList.add('is-visible')
        })

        this.isTooltipVisible = true
        this.tooltipHideTimer = null
    }

    handleMouseLeave() {
        if (!this.isTooltipVisible || this.isMobile) return
        
        this.tooltipHideTimer = setTimeout(() => {
            this.hideTooltip()
        }, 100) // 100ms delay before hiding (faster)
    }

    tooltipMouseEnter() {
        if (this.tooltipHideTimer) {
            clearTimeout(this.tooltipHideTimer)
            this.tooltipHideTimer = null

        }
    }

    tooltipMouseLeave() {

        if (!this.isMobile) {
            this.hideTooltip()
        }
    }

    hideTooltip() {
        if (this.tooltipHideTimer) {
            clearTimeout(this.tooltipHideTimer)
            this.tooltipHideTimer = null
        }

        if (!this.tooltip || !this.isTooltipVisible) return

        this.tooltip.classList.add('is-hiding')
        this.tooltip.classList.remove('is-visible')

        setTimeout(() => {
            if (this.tooltip) {
                this.tooltip.style.display = 'none'
            }
        }, 150) // Faster transition duration

        this.isTooltipVisible = false

    }
}